import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LuArrowDown } from "react-icons/lu";
import { useProjectId } from "../../../core_stores/useProjectStore";
import { areDifferentDays } from "../../../utils/dateFormatter";
import {
  ProjectChatTemporaryUserMessage,
  ProjectChatUserMessage,
} from "../../../view_stores/chat_messages/chatModels";
import {
  useAllChatElements,
  useChatElements,
  useChatMessagesActions,
  useHasMoreChatElements,
  useShouldAutoScroll,
} from "../../../view_stores/chat_messages/useChatMessagesStore";
import { useIsProjectProcessing } from "../../../view_stores/useProjectStatusStore";
import {
  useRollbackHandlerActions,
  useShouldConfirmRollback,
} from "../../../view_stores/useRollbackHandlerStore";
import ProjectChatDateSeparator from "./ProjectChatDateSeparator";
import ProjectChatHistoryLoadingIndicator from "./ProjectChatHistoryLoadingIndicator";
import { ProjectChatMessage } from "./ProjectChatMessage";

// Global event to track typewriter animation updates
const typewriterUpdateEvent = new EventTarget();

export const typewriterUpdateEmitter = {
  emit: () => {
    typewriterUpdateEvent.dispatchEvent(new Event("typewriter-update"));
  },
};

export default function ProjectChatMessages() {
  const chatElements = useChatElements();
  const isProcessing = useIsProjectProcessing();
  const shouldAutoScroll = useShouldAutoScroll();
  const projectId = useProjectId();
  const hasMoreChatElements = useHasMoreChatElements();

  const { setShouldAutoScroll, increaseNumberOfVisisbleMessages } =
    useChatMessagesActions();

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const previousProjectIdRef = useRef<string | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);

  const [shouldShowBottomShadow, setShouldShowBottomShadow] = useState(false);
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
  const isScrollingUpRef = useRef(false);

  const scrollToBottom = () => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView();
    }
  };

  const isAtBottom = () => {
    if (chatContainerRef.current) {
      const { scrollTop, clientHeight, scrollHeight } =
        chatContainerRef.current;
      // Use 50px threshold so button doesn't appear too quickly when user is visually at bottom
      return scrollTop + clientHeight >= scrollHeight - 50;
    }
  };

  useEffect(() => {
    // Only auto-scroll to bottom when new messages are added AND auto-scroll is enabled
    if (shouldAutoScroll) {
      const lastElement = chatElements[chatElements.length - 1];

      // If last message is temporary user message we have to wait for generating indicator to appear
      if (lastElement instanceof ProjectChatTemporaryUserMessage) {
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      } else {
        scrollToBottom();
      }
    }
  }, [chatElements, shouldAutoScroll]);

  // Listen for typewriter animation updates
  useEffect(() => {
    const handleTypewriterUpdate = () => {
      // Only scroll if we're processing or if auto-scroll is enabled
      if (isProcessing && shouldAutoScroll) {
        scrollToBottom();
      }
    };

    typewriterUpdateEvent.addEventListener(
      "typewriter-update",
      handleTypewriterUpdate
    );

    return () => {
      typewriterUpdateEvent.removeEventListener(
        "typewriter-update",
        handleTypewriterUpdate
      );
    };
  }, [isProcessing, shouldAutoScroll]);

  // // Handle initial scroll to bottom when messages are loaded or project changes
  useLayoutEffect(() => {
    if (chatElements.length > 0 &&
      messageEndRef.current &&
      projectId !== previousProjectIdRef.current
    ) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        // Double RAF to ensure all layouts and paints are complete
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      });
      previousProjectIdRef.current = projectId;
    }
  }, [projectId, chatElements]);

  useEffect(() => {
    if (chatContainerRef.current) {
      const handleWheel = (event: WheelEvent) => {
        // Detect scroll direction
        const deltaY = event.deltaY;
        const isScrollingUp = deltaY < 0;

        // Track if user is scrolling up to prevent handleScroll from re-enabling auto-scroll
        isScrollingUpRef.current = isScrollingUp;

        // Disable auto-scroll when scrolling up
        if (shouldAutoScroll && isScrollingUp) {
          setShouldAutoScroll(false);
        }

        // Clear the scrolling up flag after a short delay
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingUpRef.current = false;
        }, 150);
      };

      // Touch event handlers for mobile devices
      let touchStartY = 0;
      let isUserScrolling = false;

      const handleTouchStart = (event: TouchEvent) => {
        touchStartY = event.touches[0].clientY;
        isUserScrolling = true;
      };

      const handleTouchMove = (event: TouchEvent) => {
        if (!isUserScrolling) return;

        const touchY = event.touches[0].clientY;
        const deltaY = touchStartY - touchY;

        // Detect scroll direction - positive deltaY means scrolling up
        const isScrollingDown = deltaY < 0;

        // Disable auto-scroll when scrolling up (same as wheel event)
        if (shouldAutoScroll && isScrollingDown) {
          setShouldAutoScroll(false);
        }

        // Update touch start position for next move event
        touchStartY = touchY;
      };

      const handleTouchEnd = () => {
        isUserScrolling = false;
      };

      // Scroll event to detect when user manually scrolls to bottom
      const handleScroll = () => {
        const atBottom = isAtBottom();
        // Only re-enable auto-scroll if user is not actively scrolling up
        // This prevents the race condition where slow scrolling up gets snapped back
        if (chatContainerRef.current && !shouldAutoScroll && atBottom && !isScrollingUpRef.current) {
          setShouldAutoScroll(true);
        }
        // Show/hide scroll to bottom button
        setShowScrollToBottomButton(!atBottom);
      };

      chatContainerRef.current.addEventListener("wheel", handleWheel);
      chatContainerRef.current.addEventListener("touchstart", handleTouchStart);
      chatContainerRef.current.addEventListener("touchmove", handleTouchMove);
      chatContainerRef.current.addEventListener("touchend", handleTouchEnd);
      chatContainerRef.current.addEventListener("scroll", handleScroll);

      return () => {
        chatContainerRef.current?.removeEventListener("wheel", handleWheel);
        chatContainerRef.current?.removeEventListener(
          "touchstart",
          handleTouchStart
        );
        chatContainerRef.current?.removeEventListener(
          "touchmove",
          handleTouchMove
        );
        chatContainerRef.current?.removeEventListener(
          "touchend",
          handleTouchEnd
        );
        chatContainerRef.current?.removeEventListener("scroll", handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [shouldAutoScroll]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the sentinel is not fully visible (intersectionRatio < 1),
        // it means the generating indicator should be sticky
        setShouldShowBottomShadow(entry.intersectionRatio < 1 && isProcessing);
      },
      {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: "0px 0px 0px 0px",
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) observer.unobserve(sentinelRef.current);
    };
  }, [sentinelRef, isProcessing]);

  // Handle infinite scroll logic
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio >= 1 && hasMoreChatElements) {
        const container = chatContainerRef.current;
        if (container) {
          const currentScrollTop = container.scrollTop;
          const currentScrollHeight = container.scrollHeight;

          increaseNumberOfVisisbleMessages();

          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDifference = newScrollHeight - currentScrollHeight;

            container.scrollTop = currentScrollTop + heightDifference;
          });
        } else {
          increaseNumberOfVisisbleMessages();
        }
      }
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [loadMoreRef, isProcessing, hasMoreChatElements]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative pr-2">
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden pl-5 pr-2 pb-2 pt-4 space-y-4 relative"
      >
        <div ref={loadMoreRef} className="h-[0.1px] w-full" />
        <ListOfMessages />
        <div ref={messageEndRef} className="h-px w-full" />
        <ProjectChatHistoryLoadingIndicator />

        {/* Sentinel element to detect when sticky behavior should start */}
        <div ref={sentinelRef} className="h-[0.1px] w-full" />
        <GeneratingIndicator />
      </div>

      {/* Scroll to bottom button - subtle style, less prominent than submit */}
      {showScrollToBottomButton && (
        <button
          onClick={() => messageEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-1 left-1/2 -translate-x-1/2 z-30
            w-8 h-8 rounded-full bg-dark-surface-tertiary/80
            flex items-center justify-center
            hover:bg-dark-surface-tertiary transition-colors"
        >
          <LuArrowDown className="w-5 h-5 text-dark-text-secondary" />
        </button>
      )}

      <div
        className={`${shouldShowBottomShadow ? "opacity-100" : "opacity-0"}
          transition-opacity duration-300
          pointer-events-none absolute left-0 right-0 bottom-0 h-10 z-20
        bg-gradient-to-t dark:from-black from-black/40 via-black/20 dark:via-black/50 to-transparent`}
      />
    </div>
  );
}

function ListOfMessages() {
  const chatElements = useChatElements();
  const allChatElements = useAllChatElements();
  const isChatProcessing = useIsProjectProcessing();
  const shouldConfirmRollback = useShouldConfirmRollback();

  const { startRollback, reset } = useRollbackHandlerActions();

  const canShowRollbackBtn = (index: number) => {
    // Show rollback button on user messages that have a checkpoint
    const currentMsg = chatElements[index];
    if (!(currentMsg instanceof ProjectChatUserMessage)) return false;
    if (!currentMsg.checkpointId) return false;

    // Find this message's position in the FULL history (not paginated view)
    const fullHistoryIndex = allChatElements.findIndex(
      (el) => el.id === currentMsg.id
    );

    if (fullHistoryIndex <= 0) return false; // First message in full history

    // Check for previous user message in FULL history
    const previousUserMsg = allChatElements
      .slice(0, fullHistoryIndex)
      .reverse()
      .find((el) => el instanceof ProjectChatUserMessage);

    return previousUserMsg ? !!(previousUserMsg as ProjectChatUserMessage).checkpointId : false;
  };

  const onRollback = (messageIndex: number) => {
    startRollback(
      (chatElements[messageIndex] as ProjectChatUserMessage).checkpointId
    );
  };

  useEffect(() => {
    if (shouldConfirmRollback && isChatProcessing) {
      reset();
    }
  }, [isChatProcessing, shouldConfirmRollback]);

  const elements: JSX.Element[] = [];

  Object.entries(chatElements ?? []).forEach(([_, element], index) => {
    // Add date separator if this is not the first message and the day is different from the previous message
    if (index > 0) {
      const previousElement = chatElements[index - 1];
      if (areDifferentDays(previousElement.timestamp, element.timestamp)) {
        elements.push(
          <ProjectChatDateSeparator
            key={`date-separator-${element.id}`}
            timestamp={element.timestamp}
          />
        );
      }
    } else if (index === 0) {
      // Always show date separator for the first message
      elements.push(
        <ProjectChatDateSeparator
          key={`date-separator-${element.id}`}
          timestamp={element.timestamp}
        />
      );
    }

    // Add the message
    elements.push(
      <ProjectChatMessage
        key={element.id}
        message={element}
        isLast={index === (chatElements.length - 1)}
        rollbackBtn={
          canShowRollbackBtn(index)
            ? {
              isDisabled: isChatProcessing || shouldConfirmRollback,
              onClick: () => onRollback(index),
            }
            : undefined
        }
      />
    );
  });

  return <>{elements}</>;
}

function GeneratingIndicator() {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const isProcessing = useIsProjectProcessing();

  useEffect(() => {
    if (isProcessing) {
      setShouldRender(true);
      const showTimer = setTimeout(() => setIsVisible(true), 150);
      return () => clearTimeout(showTimer);
    } else {
      setIsVisible(false);
      const hideTimer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(hideTimer);
    }
  }, [isProcessing]);

  if (!shouldRender) return null;

  return (
    <div
      className={`
          flex justify-start sticky
        bottom-0 px-2
        transition-all duration-300 z-30 ${shouldRender && "h-[45px]"}`}
    >
      <div
        className={`flex items-end gap-1 rounded-full transition-all duration-300 ease-in-out ${isVisible
          ? "opacity-100 scale-100 translate-y-0"
          : "opacity-0 scale-95 translate-y-4"
          }`}
        style={{
          transformOrigin: "bottom left",
          willChange: "transform, opacity",
        }}
      >
        <div className="flex items-end justify-end gap-1 pb-1">
          <GeneratingIndicatorDot className="animate-[pulse_1.5s_ease-in-out_infinite_0ms]" />
          <GeneratingIndicatorDot className="animate-[pulse_1.5s_ease-in-out_infinite_150ms]" />
          <GeneratingIndicatorDot className="animate-[pulse_1.5s_ease-in-out_infinite_300ms]" />
        </div>
      </div>
    </div>
  );
}

function GeneratingIndicatorDot({ className }: { className: string }) {
  return <div className={`w-2 h-2 rounded-full bg-[#E91E63] ${className}`} />;
}
